import { useState, useEffect } from 'react';
import { Copy, Check, Info, ExternalLink, AlertCircle } from 'lucide-react';
import { authenticatedFetch } from '../lib/auth';

interface Props {
  studyPlanId: string | null;
  /** Hent etter "Test kobling" eller når kobling feiler — vis "URL ikke funnet" alert */
  validationError?: { code?: string; message?: string; found_url?: string } | null;
}

interface Instructions {
  study_plan_id: string;
  study_plan_title: string;
  approval_link: string;
  verification_url: string;
  instructions: string;
  paste_snippet: string;
}

/**
 * Shared component used by Moodle, TutorLMS, LearnDash, Xtramile mapping-forms.
 *
 * Shows the URL the admin MUST paste into the LMS course description before
 * the mapping can be created. Includes copy-button, instruksjons-tekst,
 * and conditional alert when validation has failed.
 */
export function LmsUrlInstructions({ studyPlanId, validationError }: Props) {
  const [data, setData] = useState<Instructions | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!studyPlanId) { setData(null); setError(null); return; }
    let aborted = false;
    setLoading(true); setError(null);
    (async () => {
      try {
        const res = await authenticatedFetch(`/api/study-plans/${studyPlanId}/lms-instructions`);
        const json = await res.json();
        if (aborted) return;
        if (!res.ok) {
          setError(json.error || 'Kunne ikke hente URL-instruksjon');
          setData(null);
          return;
        }
        setData(json);
      } catch (e: any) {
        if (!aborted) setError(e.message || 'Nettverksfeil');
      } finally {
        if (!aborted) setLoading(false);
      }
    })();
    return () => { aborted = true; };
  }, [studyPlanId]);

  async function copyUrl() {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.verification_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  }

  async function copySnippet() {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.paste_snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  }

  if (!studyPlanId) return null;
  if (loading) return (
    <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-3 text-sm text-kf-blue-800">
      Henter URL-instruksjon...
    </div>
  );
  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800 flex items-start gap-2">
      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <span>{error}</span>
    </div>
  );
  if (!data) return null;

  return (
    <div className="bg-kf-blue-50 border border-kf-blue-300 rounded-lg p-4 space-y-3">
      <div className="flex items-start gap-2">
        <Info className="w-5 h-5 text-kf-blue-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h4 className="font-semibold text-kf-blue-900">Steg 1: Legg til URL i LMS-kursbeskrivelsen</h4>
          <p className="text-sm text-kf-blue-800 mt-1">
            Før du kan opprette denne koblingen, må kursbeskrivelsen i LMS-systemet inneholde lenken til den godkjente studieplanen.
            <strong> Hvis lenken mangler vil koblingen avvises.</strong>
          </p>
        </div>
      </div>

      <div className="bg-white border border-kf-blue-200 rounded p-3">
        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">URL å lime inn</div>
        <div className="flex items-center gap-2">
          <code className="flex-1 font-mono text-sm text-gray-900 break-all bg-gray-50 px-2 py-1 rounded">
            {data.verification_url}
          </code>
          <button
            onClick={copyUrl}
            className="inline-flex items-center gap-1 px-2 py-1 bg-kf-blue-600 text-white rounded text-xs hover:bg-kf-blue-700 flex-shrink-0"
            title="Kopier URL til utklippstavlen"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? 'Kopiert' : 'Kopier'}
          </button>
        </div>
        <div className="mt-2 text-xs text-gray-600">
          Foretrekker du å lime inn med ferdig tekst?
          <button onClick={copySnippet} className="ml-1 text-kf-blue-600 hover:underline">
            Kopier som «{data.paste_snippet.length > 50 ? data.paste_snippet.slice(0, 50) + '...' : data.paste_snippet}»
          </button>
        </div>
      </div>

      <ol className="text-xs text-kf-blue-800 space-y-1 list-decimal list-inside ml-1">
        <li>Klikk <strong>Kopier</strong> over.</li>
        <li>Åpne LMS-systemet (Moodle, TutorLMS, LearnDash, Xtramile, m.fl.) og rediger kursets beskrivelse.</li>
        <li>Lim inn URL-en i beskrivelsen, og lagre der.</li>
        <li>Kom tilbake hit og fortsett med å opprette koblingen.</li>
      </ol>

      {validationError && validationError.code && (
        <div className="bg-kf-orange-50 border border-kf-orange-300 rounded p-3 text-sm text-kf-orange-900 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-semibold">Koblingen ble avvist</div>
            <div className="mt-1">{validationError.message}</div>
            {validationError.found_url && (
              <div className="mt-1 text-xs">
                Funnet URL i kursbeskrivelsen: <code className="bg-white px-1 rounded">{validationError.found_url}</code>
              </div>
            )}
            <div className="mt-2 text-[11px] text-kf-orange-700 font-mono">Feilkode: {validationError.code}</div>
          </div>
        </div>
      )}

      <div className="text-xs text-kf-blue-700 flex items-center gap-1">
        <ExternalLink className="w-3 h-3" />
        Forhåndsvis URL:{' '}
        <a href={data.verification_url} target="_blank" rel="noreferrer" className="underline">
          {data.verification_url}
        </a>
      </div>
    </div>
  );
}
